import React from "react";
import _ from "lodash";
import { Swipeable } from "react-swipeable";
import clsx from "clsx";
import Button from "@material-ui/core/Button/Button";
import DialogContent from "@material-ui/core/DialogContent/DialogContent";
import Typography from "@material-ui/core/Typography/Typography";
import DialogActions from "@material-ui/core/DialogActions/DialogActions";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import withWidth from "@material-ui/core/withWidth";
import { getConf } from "@configuratorware/configurator-frontendgui/App/configuration";
import LoadingOverlay from "../../../../../Framework/Components/LoadingOverlay";
import { T } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";
import OptionlistitemAmount from "../OptionAmount";

const styles = (theme) => ({
  detailNav: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "72px",
    transform: "scaleX(0.5)",
  },

  detailFooter: {
    clear: "both",
    padding: "10px 0 0 32px",
    textAlign: "right",

    "& button": {
      marginLeft: 10,
      background: theme.palette.primary.dark,
      color: theme.palette.primary.darkText,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#616161",
      },
    },
  },
  detailContainer: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
  },
  detailsWithImageContainer: {
    display: "grid",
    gridTemplateColumns: "minMax(300px, 4fr) 6fr",
    columnGap: 24,
  },
  priceFormatted: {
    marginTop: 32,
    marginBottom: 32,
  },
  detailsTitle: {
    fontWeight: "bold",
  },
  detailsTypography: {
    marginTop: 12,
    marginBottom: 12,
  },
  imageContainer: {
    display: "grid",
    placeItems: "start center",
  },
  image: {
    marginTop: 12,
    maxWidth: "100%",
    maxHeight: "100%",
  },
});

class Optiondetail extends React.Component {
  static propTypes = {
    details: PropTypes.object,
    optionclassification: PropTypes.object,
    classes: PropTypes.object,
    options: PropTypes.array,
    viewDetails: PropTypes.func,
    onSelect: PropTypes.func,
    OptionlistitemAmount: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func,
    ]),
    show: PropTypes.bool,
    title: PropTypes.string,
    onClose: PropTypes.func,
    LoadingComponent: PropTypes.elementType,
    width: PropTypes.string,
    isTabletDevice: PropTypes.bool,
  };

  static swipeDelta = 50;

  constructor(props) {
    super(props);

    const { details, options } = props;

    this.state = {
      currentOption: _.find(options, { identifier: details.identifier }),
      loading: false,
    };

    this.optionDetailsWithImage = getConf("optionDetailsWithImage") || false;
    this.detailContainerRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { details, options } = this.props;
    if (details.identifier !== prevProps.details.identifier) {
      this.setState({
        currentOption: _.find(options, { identifier: details.identifier }),
        loading: false,
      });
    }
  }

  getOptionData() {
    if (!this.state.currentOption) {
      return null;
    }
    const { optionclassification } = this.props;
    const { currentOption } = this.state;
    const { identifier } = currentOption;
    return (
      _.find(optionclassification.selectedoptions, { identifier }) ||
      currentOption
    );
  }

  prev = () => {
    const { details, options, viewDetails } = this.props;
    if (options.length) {
      const optionIndex = _.findIndex(options, {
        identifier: details.identifier,
      });
      const prevIndex =
        optionIndex === 0 ? options.length - 1 : optionIndex - 1;
      const prev = options[prevIndex];
      viewDetails(prev);
      this.setState({
        currentOption: prev,
        loading: true,
        loadingContainerHeight: this.detailContainerRef.clientHeight,
      });
    }
  };

  next = () => {
    const { details, options, viewDetails } = this.props;
    if (options.length) {
      const optionIndex = _.findIndex(options, {
        identifier: details.identifier,
      });
      const nextIndex =
        optionIndex === options.length - 1 ? 0 : optionIndex + 1;
      const next = options[nextIndex];
      viewDetails(next);
      this.setState({
        currentOption: next,
        loading: true,
        loadingContainerHeight: this.detailContainerRef.clientHeight,
      });
    }
  };

  swiped = (e, deltaX, deltaY) => {
    // use only horizontal swiping
    if (Math.abs(deltaX) >= Math.abs(deltaY)) {
      if (deltaX > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  };

  onAmountChange(delta) {
    const { onSelect } = this.props;
    const option = this.getOptionData();
    const amount = option.amount + delta;
    if (amount < 0) {
      return; // prevent setting below 0
    }
    onSelect(
      {
        ...option,
        amount,
      },
      true,
      true
    );
  }

  onSelect() {
    const { onSelect, onClose } = this.props;
    // just close it if it's already selected
    if (this.state.currentOption.amount > 0) {
      onClose && onClose();
    } else {
      onSelect(this.state.currentOption);
    }
  }

  renderControls() {
    const { OptionlistitemAmount } = this.props;
    const option = this.getOptionData();
    if (!option) {
      return null;
    }
    if (option.amount_is_selectable) {
      return (
        <OptionlistitemAmount
          option={option}
          onAmountChange={this.onAmountChange.bind(this)}
        />
      );
    }
    return (
      <Button
        className="primary-button"
        onClick={this.onSelect.bind(this)}
        variant="contained"
      >
        {T("optiondetail.choose")}
      </Button>
    );
  }

  render() {
    const {
      LoadingComponent,
      options,
      show,
      onClose,
      details,
      classes,
      width,
      isTabletDevice,
    } = this.props;
    const { loading, loadingContainerHeight } = this.state;
    const showNav = _.isArray(options) && options.length > 1;
    const host = getConf("network.host");
    const hasImage = !!details.detailImageExists;

    const detailsText = (
      <div>
        <Typography
          variant="h6"
          className={clsx(classes.detailsTypography, classes.detailsTitle)}
        >
          {details.abstract}
        </Typography>
        <Typography className={classes.priceFormatted}>
          {details.priceFormatted}
        </Typography>
        <Typography className={classes.detailsTypography}>
          {details.description}
        </Typography>
      </div>
    );

    const showFullScreenModal = ["xs", "sm"].includes(width) && !isTabletDevice;

    return (
      <CustomDialog
        open={show}
        dialogTitle={details.title}
        onClose={onClose}
        onCancel={onClose}
        fullScreen={showFullScreenModal}
      >
        <DialogContent>
          {loading ? (
            <div
              style={{
                height: loadingContainerHeight,
                boxSizing: "border-box",
              }}
            >
              <LoadingComponent show={loading} />
            </div>
          ) : (
            <Swipeable
              disabled={!showNav}
              trackMouse
              onSwiped={this.swiped}
              delta={Optiondetail.swipeDelta}
            >
              <div
                ref={(ref) => (this.detailContainerRef = ref)}
                className={classes.detailContainer}
              >
                {showNav && (
                  <div
                    className={clsx(classes.detailNav, "prev")}
                    onClick={this.prev}
                  />
                )}

                {hasImage ? (
                  <div
                    className={clsx({
                      [classes.detailsWithImageContainer]: !showFullScreenModal,
                    })}
                  >
                    <div className={classes.imageContainer}>
                      <img
                        className={classes.image}
                        src={`${host}/images/options/${details.imageIdentifier}_fullsize.jpg`}
                        alt={details.title}
                      />
                    </div>
                    {detailsText}
                  </div>
                ) : (
                  detailsText
                )}

                {showNav && (
                  <div
                    className={clsx(classes.detailNav, "next")}
                    onClick={this.next}
                  />
                )}
              </div>
            </Swipeable>
          )}
        </DialogContent>
        <DialogActions>
          <div className={classes.detailFooter}>{this.renderControls()}</div>
        </DialogActions>
      </CustomDialog>
    );
  }
}

Optiondetail.defaultProps = {
  OptionlistitemAmount,
  LoadingComponent: LoadingOverlay,
};

export default withStyles(styles, { name: "Optiondetail" })(
  withWidth()(Optiondetail)
);
